import { useState } from 'react';
import Icon from '../../components/AppIcon';
import Footer from '../ai-dating-service-landing-page/components/Footer';
import ContactButtons from '../ai-dating-service-landing-page/components/ContactButtons';
import { Helmet } from 'react-helmet';

interface BookSection {
  id: string;
  title: string;
  content: string | JSX.Element;
}

const FoundersMiniBookPage = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const bookSections: BookSection[] = [
    {
      id: 'intro',
      title: 'ВВЕДЕНИЕ: Невидимые частицы смерти',
      content: (
        <>
          <p className="mb-4">
            В 1847 году венский врач <a href="https://ru.wikipedia.org/wiki/%D0%97%D0%B5%D0%BC%D0%BC%D0%B5%D0%BB%D1%8C%D0%B2%D0%B5%D0%B9%D1%81,_%D0%98%D0%B3%D0%BD%D0%B0%D1%86_%D0%A4%D0%B8%D0%BB%D0%B8%D0%BF%D0%BF" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Игнац Земмельвейс</a> совершил открытие, которое стоило ему карьеры и жизни, но спасло миллионы людей. Он заметил, что в родильных отделениях женщины умирают от «<a href="https://ru.wikipedia.org/wiki/Родильная_горячка" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">родильной горячки</a>» просто потому, что врачи приходят к ним после вскрытия трупов, не помыв руки.
          </p>
          <p className="mb-4">
            Врачи того времени были в ярости. Они считали себя джентльменами, а руки джентльмена не могут быть грязными. Они не верили в «невидимые частицы», которые переносят смерть.
          </p>
          <p className="mb-4">
            Сегодня мы находимся в аналогичной ситуации. Мы все больны «социальной горячкой». Мы страдаем от хронического стресса, который разрушает наши сосуды, мозг и психику. Мы ищем причины в генетике или экологии, не замечая главного: наши взаимодействия друг с другом полны невидимого насилия.
          </p>
          <p>
            Мы прикасаемся к душам друг друга «грязными руками» принуждения, манипуляций и «необходимых компромиссов».
          </p>
        </>
      )
    },
    {
      id: 'chapter1',
      title: 'Глава 1. Великий Обман: Почему «хорошие манеры» нас убивают',
      content: (
        <>
          <p className="mb-4">
            Мы привыкли считать стресс нормой. «Такова жизнь», «Все нервничают», «Нужно быть стрессоустойчивым».
          </p>
          <p className="mb-4">
            Но давайте посмотрим на биологию. Стресс — это не черта характера. Это реакция вашего тела на вторжение. Когда вы делаете то, чего не хотите, ваш мозг посылает сигнал: «Мы в опасности, нами управляют извне!». В кровь впрыскивается <a href="https://ru.wikipedia.org/wiki/Кортизол" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">кортизол</a>. Если это происходит каждый день — вы медленно растворяете свои органы в кислоте.
          </p>
          <p className="mb-4">
            Причина 99% болезней тела и ума — это постоянное микро-насилие, которое мы совершаем над собой, чтобы «соответствовать», «зарабатывать» или «сохранять отношения». Мы считаем себя добрыми, но мы — носители инфекции. Мы заставляем других и позволяем заставлять себя.
          </p>
          <p>
            Нам нужна асептика. Нам нужно научиться различать типы действий так же четко, как хирург различает чистый инструмент и ржавый скальпель.
          </p>
        </>
      )
    },
    {
      id: 'chapter2',
      title: 'Глава 2. Формула социальной асептики (A-B-C)',
      content: (
        <>
          <p className="mb-4">
            В инженерном мире всё подчинено логике. Давайте применим её к людям. Любое взаимодействие между Человеком №1 и Человеком №2 можно классифицировать по тому, насколько оно желанно.
          </p>
          <div className="mb-4">
            <h4 className="font-semibold mb-2">Тип A — Резонанс (Любовь)</h4>
            <p className="mb-2">Оба участника искренне хотят этого действия.</p>
            <p className="mb-2"><strong>Пример:</strong> Секс по взаимной страсти, совместная игра, творчество, беседа, в которой оба увлечены.</p>
            <p><strong>Биология:</strong> Ноль стресса. Максимальный выброс гормонов счастья. Тело обновляется.</p>
          </div>
          <div className="mb-4">
            <h4 className="font-semibold mb-2">Тип B — Сделка и Вынужденность (Скрытое насилие)</h4>
            <p className="mb-2">Один из участников не хочет, но соглашается. Здесь мы совершаем самую большую ошибку, считая это «нормой».</p>
            <p className="mb-2"><strong>B1 (Сделка):</strong> Я терплю твое занудство, чтобы ты потом помог мне с делами. Это покупка результата ценой своего здоровья.</p>
            <p><strong>B2 (Выживание):</strong> Я иду на эту работу, потому что иначе нечем кормить детей. Это добровольное рабство.</p>
          </div>
          <div className="mb-4">
            <h4 className="font-semibold mb-2">Тип C — Прямое насилие (Зло)</h4>
            <p className="mb-2">Действие совершается против воли одного из участников.</p>
            <p><strong>Пример:</strong> Ограбление, приказ начальника под угрозой увольнения, крик родителя на ребенка.</p>
          </div>
          <p className="font-semibold">
            Фундаментальный вывод: Добро — это только тип A. Всё остальное (B и C) — это разные степени насилия, которые вызывают стресс и болезни.
          </p>
        </>
      )
    },
    {
      id: 'chapter3',
      title: 'Глава 3. Яд в красивой обертке: Почему «B» — это ловушка',
      content: (
        <>
          <p className="mb-4">
            Большинство людей скажут: «Жить только в типе А невозможно! Нам приходится идти на компромиссы (B1) и работать там, где не нравится (B2)».
          </p>
          <p className="mb-4">
            Это логика выживания из XIX века. В 2025 году, когда роботы могут строить дома, а ИИ — писать код, принуждение потеряло экономический смысл.
          </p>
          <p className="mb-4">
            Когда вы соглашаетесь на тип B, вы обманываете свою биологию. Ваш разум говорит: «Это выгодно». Ваше тело говорит: «Меня насилуют». И тело всегда побеждает — через рак, инфаркт или депрессию.
          </p>
          <p>
            Асептика души требует признать: любое «да», сказанное вместо «нет» — это инъекция яда.
          </p>
        </>
      )
    },
    {
      id: 'chapter4',
      title: 'Глава 4. Родители-палачи: Заражение с детства',
      content: (
        <>
          <p className="mb-4">
            Как мы учимся игнорировать насилие? Нас учат этому те, кто нас любит.
          </p>
          <p className="mb-4">
            Родитель, заталкивающий ложку супа в плачущего ребенка, — это Земмельвейс, который не помыл руки. Он уверен, что спасает ребенка от голода (доброе намерение). Но биологически он совершает акт насилия (Тип C).
          </p>
          <p className="mb-4">
            Ребенок приучается: «Твои желания не важны. Важно то, что скажет сильный». Так выращивается идеальная жертва для стресса.
          </p>
          <p>
            Чтобы разорвать этот цикл, нам нужно Оружие Добра — знания о том, как договариваться через желания (тип A), а не через приказы.
          </p>
        </>
      )
    },
    {
      id: 'chapter5',
      title: 'Глава 5. Теория Субъективной Устойчивости',
      content: (
        <>
          <p className="mb-4">
            Почему тип А так важен? Согласно одной из современных теорий, жизнь — это не просто белковая форма. Это процесс поддержания субъективной устойчивости в потоке хаоса частиц.
          </p>
          <p className="mb-4">
            Когда частицы (люди, события, информация) движутся в резонансе с вашим внутренним вектором (вашим «хочу»), ваша система становится прочнее. Вы растете.
          </p>
          <p className="mb-4">
            Когда частицы сталкиваются с вами против вашей воли (насилие), ваша устойчивость падает. Система начинает разрушаться.
          </p>
          <p>
            Жизнь стремится к устойчивости. Насилие — это <a href="https://ru.wikipedia.org/wiki/Энтропия" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">энтропия</a>, смерть. Таким образом, переход к обществу типа А — это не каприз, а следующий этап эволюции жизни во Вселенной.
          </p>
        </>
      )
    },
    {
      id: 'chapter6',
      title: 'Глава 6. Оружие Добра: Почему знания сильнее наручников',
      content: (
        <>
          <p className="mb-4">
            Мы привыкли, что порядок в мире держится на «Оружии Зла». Не пугайтесь названия — в инженерном смысле это просто инструменты принуждения: полиция, суды, тюрьмы, штрафы, армия. Все они работают по принципу Типа С: «Сделай так, как мы хотим, или тебе будет больно».
          </p>
          <p className="mb-4">
            Это работает для сдерживания хаоса, но это никогда не создает счастья. Это как пытаться вылечить инфекцию, просто заклеивая гнойники пластырем.
          </p>
          <p className="mb-4">
            Оружие Добра — это принципиально иной набор инструментов. Это:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2">
            <li><strong>Знания:</strong> Понимание того, как работает человеческая психика.</li>
            <li><strong>Лекции и мастер-классы:</strong> Обучение людей искусству находить свои истинные желания.</li>
            <li><strong>Ретриты и свидания:</strong> Создание условий, где люди могут встретиться в честности, а не в масках.</li>
          </ul>
          <p className="mb-4">
            <strong>Цель «Оружия Зла»:</strong> заставить.
          </p>
          <p className="mb-4">
            <strong>Цель «Оружия Добра»:</strong> найти решение, которого искренне хотят все участники.
          </p>
          <p>
            Если мы потратим на обучение людей искусству «действий типа А» хотя бы 1% тех денег, что тратятся на бесполезные покупки, мир очистится от стресса за одно поколение. Добро — это не слабость. Это высшая форма инженерной эффективности.
          </p>
        </>
      )
    },
    {
      id: 'chapter7',
      title: 'Глава 7. Инструменты чистоты: Зачем нам нужно «цифровое мыло»',
      content: (
        <>
          <p className="mb-4">
            Вернемся к Земмельвейсу. Мало было сказать врачам «мойте руки». Им нужно было дать мыло и воду.
          </p>
          <p className="mb-4">
            В нашем мире «мыть руки» перед общением — значит убедиться, что вы и ваш партнер действительно хотите одного и того же. Но как это сделать в огромном мегаполисе? Как не потратить годы на «1000 свиданий», пытаясь наощупь найти того, кто не будет тебя насиловать своими ожиданиями?
          </p>
          <p className="mb-4">
            Здесь на сцену выходят Технологии Асептики.
          </p>
          <p className="mb-4">
            ИИ-сервис <a href="https://aidating.love" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-semibold">aidating.love</a> — это инженерный фильтр, реализующий формулу социальной асептики (А-В-С).
          </p>
          <div className="mb-4">
            <h4 className="font-semibold mb-2">Как это работает:</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Алгоритм против лжи:</strong> ИИ анализирует не то, кем вы хотите казаться, а ваши истинные векторы устойчивости.</li>
              <li><strong>Отсекание типов B и C:</strong> Система ищет пересечения, где возникает Тип А (взаимный резонанс) еще до того, как вы сказали первое «привет».</li>
            </ul>
          </div>
          <p>
            Технологии сегодня — это те самые хлорные ванны Земмельвейса. Они позволяют нам не гадать, заражен человек «вирусом насилия» или нет, а просто создавать среду, где зараза не выживает.
          </p>
        </>
      )
    },
    {
      id: 'conclusion',
      title: 'ЗАКЛЮЧЕНИЕ: Мир без «надо». Эра тотального «хочу»',
      content: (
        <>
          <p className="mb-4">
            Мы стоим на пороге величайшего перехода в истории.
          </p>
          <p className="mb-4">
            Тысячи лет человечество жило в режиме выживания (B2). Нам нужно было заставлять себя и других, чтобы не умереть. Насилие было «необходимым злом».
          </p>
          <p className="mb-4">
            Но на дворе 2026 год. У нас есть ИИ, роботы, избыток информации и ресурсов. Эпоха «надо» закончилась.
          </p>
          <p className="mb-4">
            Насилие больше не экономично. Оно просто делает нас больными и сокращает жизнь.
          </p>
          <div className="mb-4">
            <p className="mb-2">Жить в стиле «Асептики души» — значит:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Не заставлять другого.</li>
              <li>Не позволять заставлять себя.</li>
              <li>Искать третье решение (Тип А), если первые два ведут к конфликту.</li>
            </ul>
          </div>
          <p className="mb-4">
            Это кажется невозможным только тем, кто всё еще живет с «грязными руками». Но как только вы попробуете отношения без капли принуждения, вы почувствуете, как ваше тело начинает исцеляться. Стресс уходит, потому что «охраннику» внутри вас больше не от кого защищаться.
          </p>
          <div className="mt-6 p-4 bg-primary/10 rounded-lg">
            <h4 className="font-semibold mb-3">ЗАКЛЮЧЕНИЕ: Ваш выбор</h4>
            <p className="mb-3">
              Игнаца Земмельвейса заперли в психиатрической больнице, где он и погиб. Его коллеги продолжали убивать пациенток «грязными руками» еще несколько десятилетий, прежде чем признали очевидное.
            </p>
            <p className="mb-3">
              У вас есть преимущество. Вам не нужно ждать одобрения общества.
            </p>
            <p className="mb-3">
              Вы можете внедрить «Асептику души» в свою жизнь прямо сейчас.
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-3">
              <li>Перестаньте врать себе.</li>
              <li>Используйте инструменты, которые ищут любовь (Тип А), а не сделку (Тип В).</li>
              <li>Помните: каждое ваше действие — это либо капля яда, либо капля лекарства.</li>
            </ul>
            <p className="font-semibold text-lg">
              Будьте чистыми. Выбирайте «А». Живите долго.
            </p>
          </div>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Мини-книга основателя - АСЕПТИКА ДУШИ | AI Dating Service</title>
        <meta name="description" content="Философско-инженерный трактат о добре, зле, любви и насилии. Памяти Игнаца Земмельвейса." />
      </Helmet>

      <div className="bg-gradient-to-b from-foreground/5 to-background py-12">
        <div className="max-w-content mx-auto px-4 sm:px-6">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="BookOpen" size={28} color="white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                  Мини-книга основателя
                </h1>
                <p className="text-foreground/70 text-sm mt-1">
                  Памяти <a href="https://ru.wikipedia.org/wiki/%D0%97%D0%B5%D0%BC%D0%BC%D0%B5%D0%BB%D1%8C%D0%B2%D0%B5%D0%B9%D1%81,_%D0%98%D0%B3%D0%BD%D0%B0%D1%86_%D0%A4%D0%B8%D0%BB%D0%B8%D0%BF%D0%BF" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Игнаца Земмельвейса</a>
                </p>
              </div>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">
              АСЕПТИКА ДУШИ
            </h2>
            <p className="text-foreground/70 mb-6">
              Философско-инженерный трактат о добре, зле, любви и насилии
            </p>
          </div>

          <div className="space-y-3">
            {bookSections.map((section) => (
              <div
                key={section.id}
                className="bg-background rounded-lg border border-foreground/10 overflow-hidden transition-all duration-300 hover:border-primary/30"
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-foreground/5 transition-colors"
                >
                  <span className="font-semibold text-foreground text-sm md:text-base">
                    {section.title}
                  </span>
                  <Icon
                    name={expandedSection === section.id ? "ChevronUp" : "ChevronDown"}
                    size={20}
                    className="text-foreground/60 flex-shrink-0 ml-4"
                  />
                </button>
                
                {expandedSection === section.id && (
                  <div className="px-6 pb-6 text-foreground/80 text-sm md:text-base leading-relaxed animate-fadeIn">
                    {section.content}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-primary/5 rounded-lg border-l-4 border-primary">
            <p className="text-foreground/90 mb-4">
              <strong>Автор:</strong> Николай Владимирович Буленков
            </p>
            <p className="text-foreground/80 text-sm mb-4">
              Создатель формулы социальной асептики (А-В-С) и сервиса, ее реализующего:
            </p>
            <a
              href="https://aidating.love"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold transition-colors"
            >
              <Icon name="ExternalLink" size={18} />
              <span>https://aidating.love</span>
            </a>
          </div>
        </div>
      </div>

      <Footer />
      <ContactButtons />
    </div>
  );
};

export default FoundersMiniBookPage;